/**
 * Global filters for number formatting in Vue 3
 */
import { formatNumber, formatPrice, englishToPersianDigits } from '@/utils/numberFormatter';

export const numberFilters = {
  /**
   * Format number with thousands separators
   * Usage: {{ value | formatNumber }}
   * Usage: {{ value | formatNumber(true) }} // with Persian digits
   */
  formatNumber: (value, usePersianDigits = false) => {
    return formatNumber(value, usePersianDigits);
  },

  /**
   * Format price with currency
   * Usage: {{ value | formatPrice }}
   * Usage: {{ value | formatPrice('تومان') }}
   * Usage: {{ value | formatPrice('تومان', true) }} // with Persian digits
   */
  formatPrice: (value, currency = 'تومان', usePersianDigits = false) => {
    return formatPrice(value, currency, usePersianDigits);
  },

  /**
   * Convert to Persian digits
   * Usage: {{ value | toPersianDigits }}
   */
  toPersianDigits: (value) => {
    return englishToPersianDigits(value?.toString() || '');
  },

  /**
   * Format large numbers with units (K, M, B)
   * Usage: {{ value | formatLargeNumber }}
   */
  formatLargeNumber: (value, usePersianDigits = false) => {
    if (!value || isNaN(value)) return '0';
    
    const num = parseFloat(value);
    let formatted;
    let unit = '';
    
    if (num >= 1000000000) {
      formatted = (num / 1000000000).toFixed(1);
      unit = 'B';
    } else if (num >= 1000000) {
      formatted = (num / 1000000).toFixed(1);
      unit = 'M';
    } else if (num >= 1000) {
      formatted = (num / 1000).toFixed(1);
      unit = 'K';
    } else {
      formatted = num.toString();
    }
    
    // Remove trailing .0
    formatted = formatted.replace(/\.0$/, '');
    
    const result = usePersianDigits ? englishToPersianDigits(formatted) : formatted;
    return unit ? `${result}${unit}` : result;
  },

  /**
   * Format percentage
   * Usage: {{ value | formatPercentage }}
   */
  formatPercentage: (value, decimals = 2, usePersianDigits = false) => {
    if (!value || isNaN(value)) return '0%';
    
    const formatted = parseFloat(value).toFixed(decimals);
    const result = usePersianDigits ? englishToPersianDigits(formatted) : formatted;
    return `${result}%`;
  }
};

// Helper function to install filters globally in Vue 3
export function installNumberFilters(app) {
  Object.keys(numberFilters).forEach(filterName => {
    app.config.globalProperties[`$${filterName}`] = numberFilters[filterName];
  });
}